/**
 * Start for me — curated prompt selection (offline, no AI).
 */
import { MEMORY_PROMPTS } from '../content/memoryPromptLibrary';

function inferAudience(person, storybookContext) {
  const tags = new Set(['general']);
  if (storybookContext?.memorial || person?.death_date) {
    tags.add('memorial');
  }
  if (storybookContext?.forSelf || storybookContext?.type === 'self') {
    tags.add('self');
  }
  if (person?.relationship_hint === 'child') tags.add('child');
  if (person?.relationship_hint === 'parent') tags.add('parent');
  if (person?.relationship_hint === 'grandparent') tags.add('grandparent');
  return tags;
}

function scorePrompt(prompt, audience) {
  const suitable = prompt.suitableFor || ['general'];
  let score = 0;
  for (const tag of suitable) {
    if (audience.has(tag)) score += 3;
  }
  if (suitable.includes('general')) score += 1;
  // Gentle: deprioritise loss-themed prompts unless memorial context
  if (prompt.tags?.includes('loss') && !audience.has('memorial')) score -= 2;
  return score;
}

/**
 * @param {object} opts
 * @param {object} [opts.person]
 * @param {string[]} [opts.recentPromptIds]
 * @param {object} [opts.storybookContext]
 * @param {string} [opts.excludeId]
 */
export function getMemoryPrompt({
  person = null,
  recentPromptIds = [],
  storybookContext = null,
  excludeId = null,
} = {}) {
  let pool = MEMORY_PROMPTS;
  if (excludeId) {
    pool = pool.filter((p) => p.id !== excludeId);
  }
  if (recentPromptIds.length) {
    const fresh = pool.filter((p) => !recentPromptIds.includes(p.id));
    if (fresh.length) pool = fresh;
  }
  if (!pool.length) pool = MEMORY_PROMPTS;

  const audience = inferAudience(person, storybookContext);
  const starters = pool.filter((prompt) => prompt.tier === 'starter');
  const starterFresh = starters.filter((p) => !recentPromptIds.includes(p.id));
  if (starterFresh.length) {
    const scoredStarters = starterFresh
      .map((prompt) => ({ prompt, score: scorePrompt(prompt, audience) }))
      .sort((a, b) => b.score - a.score);
    const top = scoredStarters[0]?.score ?? 0;
    const tier = scoredStarters.filter((s) => s.score >= top - 1).map((s) => s.prompt);
    return tier[Math.floor(Math.random() * tier.length)] || starterFresh[0];
  }

  const scored = pool
    .map((prompt) => ({ prompt, score: scorePrompt(prompt, audience) }))
    .sort((a, b) => b.score - a.score);

  const top = scored[0]?.score ?? 0;
  const tier = scored.filter((s) => s.score >= top - 1).map((s) => s.prompt);
  const pick = tier[Math.floor(Math.random() * tier.length)] || pool[0];
  return pick;
}
